import { useCallback, useEffect } from 'react';

import { createHook, createStore } from 'react-sweet-state';

import type { PromptInputContext } from '@atlassian/conversation-assistant-external-context';
import type { AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service';

import { type UploadedFile, useConversationFiles } from '../conversation-files';

type FileContextItem = {
	id: string;
	type: 'file';
	data: UploadedFile;
};

type ExternalContextItem = {
	id: string;
	type: 'external';
	data: PromptInputContext;
};

export type UnifiedContextItem = FileContextItem | ExternalContextItem;

const store = createStore({
	name: 'unifiedContextStore',
	initialState: {
		contexts: new Map<string, UnifiedContextItem>(),
	},
	actions: {
		syncContexts:
			(contexts: FileContextItem[] | ExternalContextItem[], type: 'file' | 'external') =>
			({ setState, getState }) => {
				const prevContexts = getState().contexts;
				const nextContext = new Map(prevContexts);
				const newContexts = new Map(contexts.map((c) => [c.id, c]));

				// Remove existing contexts of the target type that are not in the new contexts
				for (const [id, existingContext] of prevContexts) {
					if (existingContext.type === type && !newContexts.has(id)) {
						nextContext.delete(id);
					}
				}

				// Add / update all new contexts of the target type
				for (const context of contexts) {
					if (context.type === type) {
						nextContext.set(context.id, context);
					}
				}

				setState({ contexts: nextContext });
			},
		removeContextItem:
			(id: string) =>
			({ setState, getState }) => {
				const nextContext = new Map(getState().contexts);
				nextContext.delete(id);
				setState({ contexts: nextContext });
			},
	},
});

const useStore = createHook(store);

function mapContextsToContextItems<T extends 'file' | 'external'>(
	contexts: T extends 'file' ? UploadedFile[] : PromptInputContext[],
	type: T,
): T extends 'file' ? FileContextItem[] : ExternalContextItem[] {
	if (type === 'file') {
		return (contexts as UploadedFile[]).map((file) => ({
			id: `file:${file.name}`,
			type: 'file' as const,
			data: file,
		})) as any;
	} else {
		return (contexts as PromptInputContext[]).map((context) => ({
			id: `external:${context.key}`,
			type: 'external' as const,
			data: context,
		})) as any;
	}
}

export const getExternalContexts = (contexts: UnifiedContextItem[]): PromptInputContext[] => {
	return contexts
		.filter((c) => c.type === 'external')
		.map((context) => context.data as PromptInputContext);
};

export const useUnifiedContextStore = ({
	assistanceServiceParams,
}: AssistanceServiceInputParam) => {
	const [{ contexts }, { syncContexts, removeContextItem }] = useStore();
	const { files, removeFile } = useConversationFiles({ assistanceServiceParams });
	// Sync files with unified context store
	useEffect(() => {
		syncContexts(mapContextsToContextItems(files, 'file'), 'file');
	}, [files, syncContexts]);

	const removeContextItemHandler = useCallback(
		(id: string) => {
			const context = contexts.get(id);
			if (context?.type === 'file') {
				// For file contexts, delegate to file store
				const fileName = (context.data as UploadedFile).name;
				removeFile(fileName);
				// File store change will trigger sync automatically
			} else {
				removeContextItem(id);
			}
		},
		[contexts, removeFile, removeContextItem],
	);

	const syncContextsHandler = useCallback(
		<T extends 'file' | 'external'>(
			contexts: T extends 'file' ? UploadedFile[] : PromptInputContext[],
			type: T,
		) => {
			syncContexts(mapContextsToContextItems(contexts, type), type);
		},
		[syncContexts],
	);

	return {
		contexts: Array.from(contexts.values()),
		syncContexts: syncContextsHandler,
		removeContextItem: removeContextItemHandler,
	};
};
